import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SEARCH_MIN_QUERY,
  searchHitSchema,
  settingsSearchHitSchema,
  type SearchHit,
  type SearchKind,
  type SettingsSearchArea,
  type SettingsSearchHit,
  type SettingsSearchKind,
} from '@forecastify/shared';
import type { CommandItem, IconName } from '@forecastify/ui';
import { useT, type MessageKey } from '../i18n';
import { useSession } from '../session';
import { parseList } from './permissions-hook';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What ⌘K contains — item 71.                                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `CommandDialog` has existed since C2 and the ⌘K button navigated to `/tasks`, which `app.tsx`
 * described as *"the nearest true thing"* until the chunk that owned search arrived. Four kinds of
 * row, in the order somebody wants them:
 *
 *  1. **records** from `GET /search` — tasks, projects, people, reports, contracts, line items,
 *     permission-trimmed and scope-trimmed on the server;
 *  2. **actions** — the create flows, which is what a palette is for on the day nothing needs
 *     finding;
 *  3. **navigation** — every sidebar destination, reachable by typing;
 *  4. **recent items**, so the empty palette is useful rather than blank.
 *
 * ⚠️⚠️ **THIS HOOK HOLDS NO ROUTER AND NO ROUTES.** It takes `go` and the navigation rows from its
 * caller, because `app.tsx` already builds them from `ROUTES` for the sidebar and a second builder
 * is how two lists come to disagree about what exists.
 */

/** ⚠️ One per kind, so a row says what it is before it is read. */
const KIND_ICON: Readonly<Record<SearchKind, IconName>> = {
  task: 'check',
  project: 'folder',
  person: 'user-round',
  report: 'chart-column',
  contract: 'file-text',
  line_item: 'list',
};

const KIND_GROUP: Readonly<Record<SearchKind, MessageKey>> = {
  task: 'palette.group.tasks',
  project: 'palette.group.projects',
  person: 'palette.group.people',
  report: 'palette.group.reports',
  contract: 'palette.group.contracts',
  line_item: 'palette.group.lineItems',
};

/** 17 Oct 2026 · what a settings row is, at a glance. */
const SETTINGS_KIND_ICON: Readonly<Record<SettingsSearchKind, IconName>> = {
  status: 'list-checks',
  priority: 'list-checks',
  skill: 'sparkles',
  skillCategory: 'sparkles',
  skillLevel: 'sparkles',
  schedule: 'calendar-days',
  rateCard: 'credit-card',
  currency: 'globe',
  leaveType: 'sun',
  overtimeType: 'timer',
  holiday: 'calendar',
  metafield: 'tag',
  projectRole: 'user-round',
  role: 'shield-check',
  integration: 'plug',
  token: 'plug',
  passkey: 'shield-check',
  mySkill: 'sparkles',
};

const SETTINGS_KIND_GROUP: Readonly<Record<SettingsSearchKind, MessageKey>> = {
  status: 'palette.settings.status',
  priority: 'palette.settings.priority',
  skill: 'palette.settings.skill',
  skillCategory: 'palette.settings.skillCategory',
  skillLevel: 'palette.settings.skillLevel',
  schedule: 'palette.settings.schedule',
  rateCard: 'palette.settings.rateCard',
  currency: 'palette.settings.currency',
  leaveType: 'palette.settings.leaveType',
  overtimeType: 'palette.settings.overtimeType',
  holiday: 'palette.settings.holiday',
  metafield: 'palette.settings.metafield',
  projectRole: 'palette.settings.projectRole',
  role: 'palette.settings.role',
  integration: 'palette.settings.integration',
  token: 'palette.settings.token',
  passkey: 'palette.settings.passkey',
  mySkill: 'palette.settings.mySkill',
};

/**
 * ⚠️⚠️ **PER WORKSPACE, BECAUSE A RECENT TASK IN ANOTHER WORKSPACE IS A 404 WITH A FAMILIAR
 * NAME.** R-38 puts the workspace in every address; the recents list holds workspace-relative
 * hrefs, so one shared list would offer somebody rows they cannot open and — worse — rows whose
 * names tell them something about a workspace they have left.
 */
const recentsKey = (workspaceId: string): string => `forecastify.recents.${workspaceId}`;

/** Six is what fits above the fold without the empty palette becoming a screen of its own. */
const RECENTS_LIMIT = 6;

/**
 * 30 Oct 2026 · a task remembered before the search linked by KEY carries `/tasks/<uuid>`, which is
 * a 404 (UAT) — dropped, so a stale row is never offered. Opening the task again remembers it right.
 */
function isStaleTask(hit: SearchHit): boolean {
  return hit.kind === 'task' && hit.href === `/tasks/${hit.id}`;
}

function readRecents(workspaceId: string): readonly SearchHit[] {
  try {
    const raw = globalThis.localStorage.getItem(recentsKey(workspaceId));
    if (raw === null) return [];
    /* ⚠️ Parsed with the SAME schema the wire uses, not trusted. `localStorage` is the one input a
       person can edit by hand, and a malformed row here would crash the shell rather than one
       screen.
       ⚠️ Wrapped as `{ data }` because `parseList` unwraps the API's envelope — the stored value
       is a bare array, which is the right thing to store and the wrong thing to hand it. */
    /* ⚠️ Annotated `unknown`, because `JSON.parse` is typed `any` and an `any` flowing into a
       call is what `no-unsafe-assignment` refuses — correctly: the whole point here is that this
       value is untrusted until the schema has looked at it. */
    const stored: unknown = JSON.parse(raw);
    return (parseList(searchHitSchema, { data: stored }) ?? []).filter((hit) => !isStaleTask(hit));
  } catch {
    /* ⚠️ Storage throws outright in a private window and in a browser set to block site data —
       `preferences-boot.tsx` records the same trap. No recents is a fine palette. */
    return [];
  }
}

function rememberRecent(workspaceId: string, hit: SearchHit): void {
  try {
    const kept = [hit, ...readRecents(workspaceId).filter((one) => one.href !== hit.href)].slice(
      0,
      RECENTS_LIMIT,
    );
    globalThis.localStorage.setItem(recentsKey(workspaceId), JSON.stringify(kept));
  } catch {
    /* Same as above: a palette that cannot remember is not a palette that should fail. */
  }
}

export function useCommandPalette({
  open,
  navigation,
  go,
  scope = 'app',
  settings,
  actions,
}: {
  readonly open: boolean;
  /**
   * ⚠️ **`settings` searches the settings and nothing else** — 13 Oct 2026, the product owner: in
   * settings mode the search is limited to the settings' own. No server search, no recents and no
   * *new task / new project*: `navigation` is the settings sections, and that is the whole palette.
   */
  readonly scope?: 'app' | 'settings';
  /**
   * ⚠️⚠️ 17 Oct 2026 · **IN SETTINGS, THE HEADINGS AND THE ENTITIES TOO.** The product owner: the
   * settings search finds every heading of every setting and everything created inside them.
   * `headings` are the sections' card titles and tabs (`settings-search-index.ts`), shown once
   * something is typed; the entities are the server's (`GET /search/settings`), for `area`.
   */
  readonly settings?: {
    readonly area: SettingsSearchArea;
    readonly headings: readonly {
      readonly label: string;
      readonly section: string;
      readonly icon: IconName;
      readonly path: string;
    }[];
  };
  /** The sidebar's own rows, already built from `ROUTES` by the shell. */
  readonly navigation: readonly {
    readonly label: string;
    readonly icon: IconName;
    readonly path: string;
  }[];
  /** ⚠️ Takes a WORKSPACE-RELATIVE path, like every other navigation in `app.tsx`. */
  readonly go: (path: string) => void;
  /**
   * 3 Nov 2026 · which create flows this reader can finish — the list screen open to them AND the
   * create action. The *Ugrás* rows were already the sidebar's; *Új projekt* and *Új feladat* were
   * offered to everybody and led a Munkatárs to the not-available state. Absent means both.
   */
  readonly actions?: { readonly newTask: boolean; readonly newProject: boolean };
}): { items: CommandItem[]; loading: boolean; onQueryChange: (query: string) => void } {
  const t = useT();
  const session = useSession();
  const workspaceId = session.workspaceId;

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<readonly SearchHit[]>([]);
  const [settingsHits, setSettingsHits] = useState<readonly SettingsSearchHit[]>([]);
  /* ⚠️ 3 Nov 2026 · from the keystroke to the answer — the debounce included — so the palette
     says it is looking rather than "nothing matches" while it has not looked yet. */
  const [loading, setLoading] = useState(false);
  const area = settings?.area ?? null;
  const [recents, setRecents] = useState<readonly SearchHit[]>([]);

  /* ⚠️ Re-read when the palette OPENS rather than on every render: the list changes only when a
     row is chosen, and choosing one closes the palette. */
  useEffect(() => {
    if (open && workspaceId !== null) setRecents(readRecents(workspaceId));
  }, [open, workspaceId]);

  /**
   * ⚠️⚠️ **DEBOUNCED, AND THE REQUEST IN FLIGHT IS ABANDONED RATHER THAN AWAITED.** Six `ilike`
   * scans per keystroke on a real workspace is the shape that makes a search box feel broken; and
   * without the generation check, a slow response to `fo` can land after a fast one to `forecast`
   * and replace the right answer with a stale one. Both defects look like flakiness rather than
   * like a race, which is why they are written down.
   */
  const generation = useRef(0);
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < SEARCH_MIN_QUERY || workspaceId === null) {
      setHits([]);
      setSettingsHits([]);
      setLoading(false);
      generation.current += 1;
      return;
    }

    const mine = (generation.current += 1);
    setLoading(true);
    const timer = setTimeout(() => {
      void (async () => {
        /* 17 Oct 2026 · in settings, what the settings hold — the same debounce and race guard. */
        const response = await session
          .request(
            scope === 'settings'
              ? `/search/settings?q=${encodeURIComponent(trimmed)}&area=${area ?? 'workspace'}`
              : `/search?q=${encodeURIComponent(trimmed)}`,
          )
          .catch(() => null);
        if (mine !== generation.current) return;
        if (response?.ok !== true) {
          setLoading(false);
          return;
        }
        const body: unknown = await response.json().catch(() => null);
        if (mine !== generation.current) return;
        setLoading(false);
        /* ⚠️ The whole ENVELOPE, not `body.data` — `parseList` unwraps `{ data: [] }` itself, and
           handing it the array makes it return `null` for a perfectly good response. */
        if (scope === 'settings') {
          setSettingsHits(parseList(settingsSearchHitSchema, body) ?? []);
        } else {
          setHits(parseList(searchHitSchema, body) ?? []);
        }
      })();
    }, 180);

    return () => {
      clearTimeout(timer);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it searches for ever. */
  }, [query, workspaceId, scope, area]);

  const openHit = useCallback(
    (hit: SearchHit) => {
      if (workspaceId !== null) rememberRecent(workspaceId, hit);
      go(hit.href);
    },
    [go, workspaceId],
  );

  const rowFor = (hit: SearchHit, group: MessageKey): CommandItem => ({
    label: hit.label,
    ...(hit.hint === null ? {} : { hint: hit.hint }),
    icon: KIND_ICON[hit.kind],
    group: t(group),
    onSelect: () => {
      openHit(hit);
    },
  });

  const searching = query.trim().length >= SEARCH_MIN_QUERY;

  const goRows: CommandItem[] = navigation.map((entry) => ({
    label: entry.label,
    icon: entry.icon,
    group: t('palette.group.go'),
    onSelect: () => {
      go(entry.path);
    },
  }));

  const items: CommandItem[] = [
    /**
     * ⚠️⚠️ **RECORDS FIRST WHILE SOMETHING IS BEING TYPED, ACTIONS FIRST WHEN NOTHING IS.**
     * Somebody who has typed three letters is looking for a thing; somebody who has just pressed
     * ⌘K is deciding what to do. The palette's own highlight starts on row one, so this ordering
     * is what Enter does — which makes it worth getting right rather than a matter of taste.
     */
    ...hits.map((hit) => rowFor(hit, KIND_GROUP[hit.kind])),

    /* ⚠️ The recents are hidden the moment a query exists: they are not results, and mixing them
       into a filtered list would offer rows that match nothing somebody typed. */
    ...(searching ? [] : recents.map((hit) => rowFor(hit, 'palette.group.recent'))),

    /**
     * ⚠️⚠️ **`?new=1` RATHER THAN A ROUTE, AND THE CONVENTION IS ALREADY THE PRODUCT'S.**
     * `delivery-route.tsx` has read this parameter since the sidebar gained its `+`: creating
     * something is a DIALOG on a list screen, and a query parameter is how one part of the app
     * asks for one without knowing anything about that screen's internals. The palette is the
     * second caller and needed no new mechanism; `tasks-route.tsx` gained the same effect.
     */
    ...(actions?.newTask === false
      ? []
      : [
          {
            label: t('palette.action.newTask'),
            icon: 'plus' as const,
            group: t('palette.group.actions'),
            onSelect: () => {
              go('/tasks?new=1');
            },
          },
        ]),
    ...(actions?.newProject === false
      ? []
      : [
          {
            label: t('palette.action.newProject'),
            icon: 'plus' as const,
            group: t('palette.group.actions'),
            onSelect: () => {
              go('/projects?new=1');
            },
          },
        ]),

    ...goRows,
  ];

  /**
   * 17 Oct 2026 · the settings palette: the sections, then — once something is typed — the headings
   * inside them and what they hold. ⚠️ The headings wait for a query: fifty of them on an empty
   * palette would bury the sections somebody opened it to pick from.
   */
  const settingsItems: CommandItem[] = [
    ...goRows,
    ...(query.trim() === ''
      ? []
      : (settings?.headings ?? []).map((heading) => ({
          label: heading.label,
          hint: heading.section,
          icon: heading.icon,
          group: t('palette.group.headings'),
          onSelect: () => {
            go(heading.path);
          },
        }))),
    ...settingsHits.map((hit) => ({
      label: hit.label,
      ...(hit.hint === null ? {} : { hint: hit.hint }),
      icon: SETTINGS_KIND_ICON[hit.kind],
      group: t(SETTINGS_KIND_GROUP[hit.kind]),
      onSelect: () => {
        go(hit.href);
      },
    })),
  ];

  return {
    items: scope === 'settings' ? settingsItems : items,
    loading,
    onQueryChange: setQuery,
  };
}
